import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Search, Power, Pencil, Trash2, Star, X, Save,
  Loader2, User, RefreshCw, CheckCircle, KeyRound, Eye, EyeOff, Copy
} from 'lucide-react';
import { MUNICIPIOS, AREAS } from '../../utils/cidades';

// ── Modal de confirmação de exclusão ─────────────────────────
function ModalConfirmar({ candidato, onConfirmar, onCancelar, excluindo }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Trash2 size={22} className="text-red-500"/>
        </div>
        <h3 className="font-sora font-bold text-center text-acre-dark mb-2">Excluir candidato?</h3>
        <p className="text-sm text-gray-500 text-center mb-1">Você está prestes a excluir permanentemente:</p>
        <p className="text-sm font-semibold text-center text-acre-dark mb-5">{candidato?.nome}</p>
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700 mb-5">
          ⚠️ Esta ação é <strong>irreversível</strong>. O currículo, fotos, experiências e histórico serão excluídos.
        </div>
        <div className="flex gap-3">
          <button onClick={onCancelar} className="btn-outline flex-1 py-2.5 text-sm justify-center">
            Cancelar
          </button>
          <button onClick={onConfirmar} disabled={excluindo}
            className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-bold transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {excluindo ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
            Excluir
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal de edição de candidato ──────────────────────────────
function ModalEditar({ candidato, onSalvar, onFechar, salvando }) {
  const [form, setForm] = useState({
    nome:            candidato.nome || '',
    email:           candidato.email || '',
    cidade:          candidato.cidade || '',
    whatsapp:        candidato.whatsapp || '',
    selo_verificado: candidato.selo_verificado || false,
    areas_interesse: candidato.areas_interesse || [],
  });

  const AREAS_LIST = typeof AREAS !== 'undefined' ? AREAS : [
    'Tecnologia da Informação','Administração e Gestão','Saúde e Enfermagem',
    'Educação e Ensino','Comércio e Vendas','Engenharia e Construção',
    'Agronegócio','Direito e Jurídico','Contabilidade e Finanças',
    'Comunicação e Marketing','Logística e Transporte',
  ];

  const toggleArea = (area) => {
    setForm(p => ({
      ...p,
      areas_interesse: p.areas_interesse.includes(area)
        ? p.areas_interesse.filter(a => a !== area)
        : [...p.areas_interesse, area],
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-acre-dark to-acre-green p-5 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <User size={18} className="text-white"/>
            <div>
              <p className="font-sora font-bold text-white">Editar Candidato</p>
              <p className="text-white/60 text-xs">{candidato.email}</p>
            </div>
          </div>
          <button onClick={onFechar} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X size={14} className="text-white"/>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="label">Nome completo *</label>
              <input className="input" value={form.nome} onChange={e => setForm(p => ({...p, nome: e.target.value}))}/>
            </div>
            <div className="col-span-2">
              <label className="label">E-mail *</label>
              <input className="input" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))}/>
            </div>
            <div>
              <label className="label">WhatsApp</label>
              <input className="input" value={form.whatsapp} onChange={e => setForm(p => ({...p, whatsapp: e.target.value}))} placeholder="68 9 9999-9999"/>
            </div>
            <div>
              <label className="label">Cidade</label>
              <select className="input" value={form.cidade} onChange={e => setForm(p => ({...p, cidade: e.target.value}))}>
                <option value="">Selecione...</option>
                {MUNICIPIOS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          {/* Áreas de interesse */}
          <div>
            <label className="label">Áreas de interesse</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {AREAS_LIST.map(area => (
                <button key={area} type="button" onClick={() => toggleArea(area)}
                  className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-all ${
                    form.areas_interesse.includes(area)
                      ? 'bg-acre-green text-white border-acre-green'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-acre-green hover:text-acre-green'
                  }`}>
                  {area}
                </button>
              ))}
            </div>
          </div>

          {/* Selo Ouro */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-700">⭐ Candidato Ouro</p>
              <p className="text-xs text-gray-400 mt-0.5">Concede o selo de candidato verificado</p>
            </div>
            <button type="button"
              onClick={() => setForm(p => ({...p, selo_verificado: !p.selo_verificado}))}
              className={`w-10 h-6 rounded-full transition-colors relative flex-shrink-0 ${form.selo_verificado ? 'bg-yellow-400' : 'bg-gray-200'}`}>
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.selo_verificado ? 'translate-x-4' : 'translate-x-0.5'}`}/>
            </button>
          </div>

          <div className="flex gap-3 pt-1">
            <button onClick={onFechar} className="btn-outline flex-1 py-2.5 text-sm justify-center">
              Cancelar
            </button>
            <button onClick={() => onSalvar(form)} disabled={salvando}
              className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {salvando ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
              Salvar alterações
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Modal reset de senha ──────────────────────────────────────
function ModalResetSenha({ usuario, onFechar }) {
  const [novaSenha, setNovaSenha] = useState('');
  const [verSenha,  setVerSenha]  = useState(false);
  const [salvando,  setSalvando]  = useState(false);
  const [resultado, setResultado] = useState(null);

  const gerar = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#$!';
    let s = '';
    for (let i = 0; i < 10; i++) s += chars[Math.floor(Math.random() * chars.length)];
    setNovaSenha(s);
  };

  const salvar = async () => {
    if (!novaSenha || novaSenha.length < 8) return toast.error('Mínimo 8 caracteres.');
    setSalvando(true);
    try {
      const { data } = await api.post(`/admin/usuarios/${usuario.id}/resetar-senha`, { nova_senha: novaSenha });
      setResultado(data.nova_senha);
      toast.success(data.mensagem);
    } catch (err) { toast.error(err.response?.data?.erro || 'Erro ao resetar.'); }
    setSalvando(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
            <KeyRound size={17} className="text-blue-600"/>
          </div>
          <div>
            <p className="font-sora font-bold text-acre-dark">Resetar Senha</p>
            <p className="text-xs text-gray-400 truncate max-w-[200px]">{usuario.nome}</p>
          </div>
          <button onClick={onFechar} className="ml-auto w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
            <X size={13}/>
          </button>
        </div>

        {!resultado ? (
          <div className="space-y-3">
            <div>
              <label className="label">Nova senha *</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input className="input pr-8 font-mono text-sm" type={verSenha ? 'text' : 'password'}
                    value={novaSenha} onChange={e => setNovaSenha(e.target.value)}
                    placeholder="Mín. 8 caracteres"/>
                  <button type="button" onClick={() => setVerSenha(v => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400">
                    {verSenha ? <EyeOff size={13}/> : <Eye size={13}/>}
                  </button>
                </div>
                <button onClick={gerar} className="btn-outline text-xs px-2.5 flex-shrink-0" title="Gerar senha aleatória">
                  🎲
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">O candidato deverá trocar a senha no próximo acesso.</p>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={onFechar} className="btn-outline flex-1 py-2 text-sm justify-center">Cancelar</button>
              <button onClick={salvar} disabled={salvando || novaSenha.length < 8}
                className="btn-primary flex-1 py-2 text-sm flex items-center justify-center gap-1.5 disabled:opacity-60">
                {salvando ? <Loader2 size={13} className="animate-spin"/> : <KeyRound size={13}/>}
                Resetar
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
              <p className="text-xs text-green-600 font-semibold mb-1">✅ Senha redefinida com sucesso!</p>
              <p className="text-xs text-gray-500 mb-2">Comunique a nova senha ao usuário:</p>
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-2">
                <code className="flex-1 font-mono text-sm text-acre-dark">{resultado}</code>
                <button onClick={() => { navigator.clipboard.writeText(resultado); toast.success('Copiado!'); }}
                  className="text-gray-400 hover:text-acre-green flex-shrink-0">
                  <Copy size={14}/>
                </button>
              </div>
            </div>
            <button onClick={onFechar} className="btn-primary w-full py-2.5 text-sm justify-center">Fechar</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────
export default function AdminCandidatos() {
  const [candidatos, setCandidatos] = useState([]);
  const [busca,      setBusca]      = useState('');
  const [somenteOuro,setSomenteOuro]= useState(false);
  const [loading,    setLoading]    = useState(true);
  const [editando,   setEditando]   = useState(null);
  const [confirmEx,  setConfirmEx]  = useState(null);
  const [excluindo,  setExcluindo]  = useState(null);
  const [salvando,   setSalvando]   = useState(false);
  const [toggling,   setToggling]   = useState(null);
  const [resetando,  setResetando]  = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (busca)       q.set('busca', busca);
    if (somenteOuro) q.set('selo', 'true');
    try {
      const { data } = await api.get(`/admin/candidatos?${q}`);
      setCandidatos(data);
    } catch { toast.error('Erro ao carregar candidatos.'); }
    setLoading(false);
  }, [busca, somenteOuro]);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Toggle ativo/inativo ──────────────────────────────────
  const toggleAtivo = async (c) => {
    setToggling(c.id);
    try {
      await api.put(`/admin/usuarios/${c.id}/ativo`, { ativo: !c.ativo });
      toast.success(c.ativo ? `${c.nome} desativado.` : `${c.nome} ativado.`);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao alterar status.');
    }
    setToggling(null);
  };

  // ── Salvar edição ─────────────────────────────────────────
  const salvarEdicao = async (form) => {
    setSalvando(true);
    try {
      await api.put(`/admin/usuarios/${editando.id}`, form);
      // Selo separado
      await api.put(`/admin/candidatos/${editando.id}/selo`, { selo: form.selo_verificado });
      toast.success('Candidato atualizado!');
      setEditando(null);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao salvar.');
    }
    setSalvando(false);
  };

  // ── Excluir ───────────────────────────────────────────────
  const confirmarExclusao = async () => {
    setExcluindo(confirmEx.id);
    try {
      const { data } = await api.delete(`/admin/usuarios/${confirmEx.id}`);
      toast.success(data.mensagem);
      setConfirmEx(null);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao excluir.');
    }
    setExcluindo(null);
  };

  const total = candidatos.length;

  return (
    <Layout title="Candidatos" subtitle="Gerencie candidatos, perfis e selos de verificação">

      {editando  && <ModalEditar candidato={editando} onSalvar={salvarEdicao} onFechar={() => setEditando(null)} salvando={salvando}/>}
      {confirmEx && <ModalConfirmar candidato={confirmEx} onConfirmar={confirmarExclusao} onCancelar={() => setConfirmEx(null)} excluindo={!!excluindo}/>}
      {resetando && <ModalResetSenha usuario={resetando} onFechar={() => setResetando(null)}/>}

      {/* Filtros */}
      <div className="flex gap-3 mb-5 flex-wrap items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input className="input pl-7 py-2 text-sm" placeholder="Buscar por nome ou e-mail..."
            value={busca} onChange={e => setBusca(e.target.value)}/>
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 select-none">
          <input type="checkbox" checked={somenteOuro} onChange={e => setSomenteOuro(e.target.checked)}
            className="w-4 h-4 accent-yellow-400"/>
          ⭐ Somente Ouro
        </label>
        <button onClick={carregar} className="p-2 rounded-lg border border-gray-200 text-gray-400 hover:text-acre-green hover:border-acre-green transition-colors">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''}/>
        </button>
        <p className="text-xs text-gray-400 ml-auto">{total} candidato{total !== 1 ? 's' : ''}</p>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin text-acre-green"/>
        </div>
      ) : (
        <div className="space-y-2.5">
          {candidatos.map(c => (
            <div key={c.id}
              className={`card p-4 flex items-center gap-3 transition-all ${!c.ativo ? 'opacity-50 bg-gray-50' : 'hover:shadow-sm'}`}>

              {/* Avatar */}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                c.selo_verificado ? 'bg-yellow-100 text-yellow-700 ring-2 ring-yellow-300' : 'bg-acre-light text-acre-green'
              }`}>
                {c.nome?.charAt(0).toUpperCase()}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-sora font-semibold text-acre-dark text-sm">{c.nome}</span>
                  {c.selo_verificado && <span className="text-xs bg-yellow-50 text-yellow-600 border border-yellow-200 px-1.5 py-0.5 rounded-full font-bold">⭐ Ouro</span>}
                  {!c.ativo && <span className="text-xs bg-gray-100 text-gray-500 border border-gray-200 px-1.5 py-0.5 rounded-full font-semibold">Inativo</span>}
                </div>
                <p className="text-xs text-gray-400 mt-0.5 truncate">
                  {c.email}{c.cidade ? ` · ${c.cidade}` : ''}
                </p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {(c.areas_interesse || []).slice(0, 2).map(a => (
                    <span key={a} className="text-xs bg-acre-light text-acre-green border border-acre-green/20 px-1.5 py-0.5 rounded-full">{a}</span>
                  ))}
                  <span className={`text-xs font-bold ${c.porcentagem_perfil === 100 ? 'text-acre-green' : 'text-gray-400'}`}>
                    {c.porcentagem_perfil || 0}% perfil
                  </span>
                </div>
              </div>

              {/* Ações */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {/* Ativar / Desativar */}
                <button onClick={() => toggleAtivo(c)} disabled={toggling === c.id}
                  title={c.ativo ? 'Desativar candidato' : 'Ativar candidato'}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all border ${
                    c.ativo
                      ? 'border-red-200 text-red-400 hover:bg-red-50 hover:text-red-600'
                      : 'border-green-200 text-green-500 hover:bg-green-50'
                  }`}>
                  {toggling === c.id ? <Loader2 size={13} className="animate-spin"/> : <Power size={13}/>}
                </button>

                {/* Resetar senha */}
                <button onClick={() => setResetando(c)} title="Resetar senha"
                  className="w-8 h-8 rounded-lg border border-indigo-200 text-indigo-500 hover:bg-indigo-50 flex items-center justify-center transition-all">
                  <KeyRound size={13}/>
                </button>

                {/* Editar */}
                <button onClick={() => setEditando(c)} title="Editar candidato"
                  className="w-8 h-8 rounded-lg border border-blue-200 text-blue-500 hover:bg-blue-50 flex items-center justify-center transition-all">
                  <Pencil size={13}/>
                </button>

                {/* Excluir */}
                <button onClick={() => setConfirmEx(c)} title="Excluir candidato"
                  className="w-8 h-8 rounded-lg border border-red-200 text-red-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition-all">
                  <Trash2 size={13}/>
                </button>
              </div>
            </div>
          ))}

          {!candidatos.length && (
            <div className="card p-12 text-center">
              <User size={32} className="text-gray-200 mx-auto mb-3"/>
              <p className="text-gray-400">Nenhum candidato encontrado.</p>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
